import { formatDistanceStrict } from 'date-fns'
import { isEmpty, map, find, isUndefined, filter, has, get } from 'lodash'
import { getExtension } from '../../../utils'
import { imageRequirements } from '../../../constants'

// STY-730: a photo only counts once it has actually landed. Placeholders that
// are still uploading (uploaded === false) or that failed must not satisfy the
// minimum -- before this, Submit could enable on photos that vanished on reload.
export function countLandedImages (images) {
  return filter(images || [], (image) => !image.failed && !(has(image, 'uploaded') && !image.uploaded)).length
}

// Uploads still in flight: not yet counted toward the minimum, but they will
// land, so they count toward the 300 maximum and Submit waits for them.
export function countPendingImages (images) {
  return filter(images || [], (image) => !image.failed && has(image, 'uploaded') && !image.uploaded).length
}

export function requiredImages (submission) {
  let numImages = find(imageRequirements, ['type', submission.type])
  if (isUndefined(numImages)) numImages = find(imageRequirements, ['type', 'default'])
  return numImages.count
}

function emptyRequiredPrompts (additionalInfo) {
  const empty = []
  if (isEmpty(additionalInfo)) return empty
  map(get(additionalInfo, 'data.sections', []), (section) => {
    map(section.fields, (dataPoint) => {
      if (dataPoint.type === 'Required' && (!dataPoint.text || dataPoint.text.length === 0)) empty.push(dataPoint.name)
    })
  })
  return empty
}

/**
 * STY-730 / spec R7: the single source of truth for "can this be submitted".
 * Both Submit buttons, the sidebar checklist and the missing-items dialog use
 * it, so they can no longer disagree. The old checklist showed Location but
 * isValid never checked it; Matt (2026-10-14) made it a real requirement --
 * every one of 825 full submissions Apr-Sep 2026 already had one. Rules: title,
 * location, (description OR every Required prompt), >= 1 credit, and between
 * the type's minimum and 300 photos.
 */
export function getChecklist (submission, credits, images, additionalInfo) {
  const minimum = requiredImages(submission)
  const count = countLandedImages(images)
  const pending = countPendingImages(images)
  const emptyPrompts = emptyRequiredPrompts(additionalInfo)
  const detailsDone = Boolean(submission.description) || emptyPrompts.length === 0
  const hasCredit = Boolean(credits && credits.length)
  const total = count + pending
  const tooMany = total > 300

  let imagesMissing = null
  if (tooMany) imagesMissing = `Remove ${total - 300} photo${total - 300 === 1 ? '' : 's'} (the maximum is 300)`
  else if (count < minimum) imagesMissing = `Upload ${minimum - count} more photo${minimum - count === 1 ? '' : 's'} (${count} of ${minimum} so far)`
  else if (pending > 0) imagesMissing = `Wait for ${pending} photo${pending === 1 ? '' : 's'} still uploading to finish`

  return [
    {
      key: 'title',
      label: 'A Title For Your Submission',
      done: Boolean(submission.title),
      missing: 'Add a title for your submission'
    },
    {
      key: 'location',
      label: 'The Event Location',
      done: Boolean(submission.location && String(submission.location).trim()),
      missing: 'Add the event location'
    },
    {
      key: 'details',
      label: submission.id > 358705 ? 'All Required Details' : 'A Detailed Description of the Day',
      done: detailsDone,
      missing: emptyPrompts.length ? `Fill in the required details: ${emptyPrompts.join(', ')}` : 'Add a description of the day'
    },
    {
      key: 'images',
      label: `Photos: ${count} / ${minimum}${minimum > 1 ? ' (max 300)' : ''}`,
      done: count >= minimum && !tooMany && pending === 0,
      missing: imagesMissing
    },
    {
      key: 'credits',
      label: 'Credits for All Vendors Involved',
      done: hasCredit,
      missing: 'Add at least one vendor or venue credit'
    }
  ]
}

export function missingRequirements (submission, credits, images, additionalInfo) {
  return filter(getChecklist(submission, credits, images, additionalInfo), (item) => !item.done)
}

export function isValid (submission, credits, images, videos, additionalInfo) {
  return missingRequirements(submission, credits, images, additionalInfo).length === 0
}

export function lastSaved (time = new Date()) {
  return formatDistanceStrict(new Date(time), new Date())
}

export function verifyImage (file, isAdmin) {
  if (file.width < 1024 && !isAdmin) return Object.assign(file, { valid: false, reason: 'is too small. Images must be at least 1024px in width.' })
  else if(file.size > 10485760 && !isAdmin) return Object.assign(file, { valid: false, reason: 'is too large. Images must be less than 10 MB in file size.' })
  else return Object.assign(file, { valid: true, uploaded: false })
}

export function uniqid (prefix, moreEntropy) {
  if (typeof prefix === 'undefined') prefix = ''
  let retId
  let _formatSeed = function (seed, reqWidth) {
    seed = parseInt(seed, 10).toString(16)
    if (reqWidth < seed.length) return seed.slice(seed.length - reqWidth)
    if (reqWidth > seed.length) return Array(1 + (reqWidth - seed.length)).join('0') + seed
    return seed
  }
  let uniqidSeed = Math.floor(Math.random() * 0x75bcd15)
  uniqidSeed++
  retId = prefix
  retId += _formatSeed(parseInt(new Date().getTime() / 1000, 10), 8)
  retId += _formatSeed(uniqidSeed, 5)
  if (moreEntropy) retId += (Math.random() * 10).toFixed(8).toString()
  return retId
}

export function uniqueFilename (filename) {
  return `${uniqid()}.${getExtension(filename)}`
}
